import AuthorizationService from '../../sdk/services/AuthService';
import { Usuario } from '../../sdk/@types';

/**
 * `GET /auth/{token}` — **uma vez por token**, compartilhado por todo mundo que
 * precisa saber quem está logado.
 *
 * Havia duas leituras do mesmo endpoint convivendo, e nenhuma sabia da outra:
 * o `useProfileStore` (perfil, para as guardas de rota e para o gate de
 * `/moderation/stats`) e o thunk `usuario/getUser` do Redux (usuário inteiro,
 * que alimenta o menu). Um carregamento frio gastava duas chamadas idênticas —
 * e `Certame.view`/`QuestionList.view`, que também disparam o thunk na
 * montagem, gastavam mais uma por visita quando o Redux ainda estava vazio.
 *
 * A chave do cache é **o token**, não um booleano: o "Sair" do Sidebar só
 * navega para `/login` (não limpa storage nem recarrega o documento), então um
 * segundo login pode acontecer com a árvore React montada. Token novo é sessão
 * nova e refaz a leitura; é a mesma premissa de `useSessionKey`.
 *
 * Uma falha **não** fica cacheada: o próximo pedido tenta de novo. Quem dedupa
 * por carregamento de página é o `useProfileStore` (`loadedOnce`), não isto.
 */

let cache: { token: string; user: Promise<Usuario.Usuario> } | null = null;

export function fetchLoggedUser(token: string): Promise<Usuario.Usuario> {
  if (cache?.token === token) return cache.user;

  const user = AuthorizationService.getUser(token).catch((raw) => {
    if (cache?.token === token) cache = null;
    throw raw;
  });

  cache = { token, user };
  return user;
}

/** Esquece o usuário lido — usado quando a sessão é derrubada. */
export function clearLoggedUser() {
  cache = null;
}
